import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  LinearProgress,
  Alert,
  CircularProgress,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  PlayArrow,
  Edit,
  Add,
  Timeline,
  Assignment,
  Speed,
  CheckCircle,
  Warning,
  Refresh,
  Cancel,
  Publish,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../store/store';
import {
  fetchWorkOrders,
  createWorkOrder,
  updateProductionQuantity,
  clearError,
} from '../../store/slices/productionSlice';

const API = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100').replace(/\/api\/v\d+\/?$/, '');
// Work-order lifecycle calls go through the API gateway (never a service port directly)
const WO_API_BASE = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1'}/work-orders`;

// Local WorkOrder type matches the slice's normalised shape
interface WorkOrder {
  id: string;
  orderNumber: string;
  productCode: string;
  productName: string;
  quantity: number;
  completedQuantity: number;
  status: 'draft' | 'planned' | 'released' | 'started' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  startDate: string;
  dueDate: string;
  workCenter: string;
  assignedOperator?: string;
}

interface WorkOrderOperation {
  id: string;
  sequence: number;
  operationName: string;
  workCenter: string;
  status: 'pending' | 'planned' | 'in_progress' | 'completed' | 'cancelled';
  plannedQuantity: number;
  actualQuantity: number;
}

interface ProcessRoute {
  id: string;
  name: string;
  routeCode: string;
  operations?: Array<{ sequence: number; operationName: string }>;
}

// Default form state for the Create Work Order dialog
const EMPTY_FORM: Partial<WorkOrder> = {
  orderNumber: '',
  productCode: '',
  productName: '',
  quantity: 0,
  status: 'draft',
  priority: 'medium',
  startDate: '',
  dueDate: '',
  workCenter: '',
  assignedOperator: '',
};

const Production: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { workOrders, isLoading, error } = useSelector(
    (state: RootState) => state.production
  );

  const [selectedTab, setSelectedTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedWorkOrder, setSelected] = useState<WorkOrder | null>(null);
  const [productionQuantity, setProductionQuantity] = useState(0);
  const [newWorkOrder, setNewWorkOrder] = useState<Partial<WorkOrder>>(EMPTY_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // ---- Task 1: Cancel dialog state ----
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelTargetId, setCancelTargetId] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState<string | null>(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // ---- Task 2: Operations detail state ----
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailWorkOrder, setDetailWorkOrder] = useState<WorkOrder | null>(null);
  const [operations, setOperations] = useState<WorkOrderOperation[]>([]);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [operationsError, setOperationsError] = useState<string | null>(null);
  // Per-operation actual quantity input for complete
  const [opActualQty, setOpActualQty] = useState<Record<string, number>>({});

  // ---- Task 3: Process route state ----
  const [processRoutes, setProcessRoutes] = useState<ProcessRoute[]>([]);
  const [processRoutesLoading, setProcessRoutesLoading] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<ProcessRoute | null>(null);
  const [selectedRouteDetail, setSelectedRouteDetail] = useState<ProcessRoute | null>(null);
  const [routeDetailLoading, setRouteDetailLoading] = useState(false);

  // Fetch real work orders on mount
  useEffect(() => {
    dispatch(fetchWorkOrders());
  }, [dispatch]);

  // ---- Snackbar helper ----
  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  // ---- Auth helpers ----
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const getUserIdFromToken = (): string => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return '';
      const p = JSON.parse(atob(token.split('.')[1]));
      return p.sub || p.userId || '';
    } catch {
      return '';
    }
  };

  // ---- Lifecycle API functions ----

  const releaseWorkOrder = async (id: string) => {
    try {
      const res = await fetch(`${WO_API_BASE}/${id}/release`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw { message: json?.error?.message || 'Failed to release work order' };
      }
      showSnackbar('Work order released successfully', 'success');
      dispatch(fetchWorkOrders());
    } catch (err: any) {
      showSnackbar(err?.message || 'Failed to release work order', 'error');
    }
  };

  const startWorkOrder = async (id: string) => {
    try {
      const res = await fetch(`${WO_API_BASE}/${id}/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw { message: json?.error?.message || 'Failed to start work order' };
      }
      showSnackbar('Work order started successfully', 'success');
      dispatch(fetchWorkOrders());
    } catch (err: any) {
      showSnackbar(err?.message || 'Failed to start work order', 'error');
    }
  };

  const completeWorkOrder = async (id: string) => {
    try {
      const res = await fetch(`${WO_API_BASE}/${id}/complete`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw { message: json?.error?.message || 'Failed to complete work order' };
      }
      showSnackbar('Work order completed successfully', 'success');
      dispatch(fetchWorkOrders());
    } catch (err: any) {
      showSnackbar(err?.message || 'Failed to complete work order', 'error');
    }
  };

  // ---- Task 1: Cancel with reason dialog ----

  const handleOpenCancelDialog = (workOrder: WorkOrder) => {
    setCancelTargetId(workOrder.id);
    setCancelReason('');
    setCancelReasonError(null);
    setCancelDialogOpen(true);
  };

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
    setCancelTargetId('');
    setCancelReason('');
    setCancelReasonError(null);
  };

  const handleConfirmCancel = async () => {
    if (cancelReason.trim().length < 10) {
      setCancelReasonError('Reason must be at least 10 characters');
      return;
    }
    setCancelLoading(true);
    try {
      const userId = getUserIdFromToken();
      const res = await fetch(`${WO_API_BASE}/${cancelTargetId}/cancel`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: cancelReason.trim(), cancelledBy: userId }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw { message: json?.error?.message || 'Failed to cancel work order' };
      }
      handleCloseCancelDialog();
      showSnackbar('Work order cancelled', 'success');
      dispatch(fetchWorkOrders());
    } catch (err: any) {
      showSnackbar(err?.message || 'Failed to cancel work order', 'error');
    } finally {
      setCancelLoading(false);
    }
  };

  // ---- Task 2: Operations fetch and per-operation actions ----

  const fetchOperations = useCallback(async (woId: string) => {
    setOperationsLoading(true);
    setOperationsError(null);
    setOperations([]);
    try {
      const res = await fetch(`${WO_API_BASE}/${woId}/operations`, {
        headers: getAuthHeaders(),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw { message: json?.error?.message || 'Failed to fetch operations' };
      }
      const raw: any[] = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json?.operations)
        ? json.operations
        : [];
      setOperations(
        raw.map((op: any) => ({
          id: String(op.id ?? op._id ?? ''),
          sequence: Number(op.operationSequence ?? op.sequence ?? op.sequenceNumber ?? 0),
          operationName:
            op.operationDescription ?? op.operationName ?? op.name ?? op.operation_name ??
            op.operationCode ?? '',
          workCenter: op.workCenterCode ?? op.workCenter ?? op.work_center ?? '',
          status: op.status ?? 'pending',
          plannedQuantity: Number(op.quantityPlanned ?? op.plannedQuantity ?? op.planned_quantity ?? 0),
          actualQuantity: Number(op.quantityCompleted ?? op.actualQuantity ?? op.actual_quantity ?? 0),
        }))
      );
    } catch (err: any) {
      setOperationsError(err?.message || 'Failed to load operations');
    } finally {
      setOperationsLoading(false);
    }
  }, []);

  const handleOpenDetail = (workOrder: WorkOrder) => {
    setDetailWorkOrder(workOrder);
    setDetailDialogOpen(true);
    fetchOperations(workOrder.id);
  };

  const handleOperationStart = async (woId: string, opId: string) => {
    try {
      const userId = getUserIdFromToken();
      const res = await fetch(`${WO_API_BASE}/${woId}/operations/${opId}/start`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ startedBy: userId }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw { message: json?.error?.message || 'Failed to start operation' };
      }
      showSnackbar('Operation started', 'success');
      fetchOperations(woId);
    } catch (err: any) {
      showSnackbar(err?.message || 'Failed to start operation', 'error');
    }
  };

  const handleOperationComplete = async (woId: string, opId: string) => {
    try {
      const userId = getUserIdFromToken();
      const actualQuantity = opActualQty[opId] ?? 0;
      const res = await fetch(`${WO_API_BASE}/${woId}/operations/${opId}/complete`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ completedBy: userId, actualQuantity }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw { message: json?.error?.message || 'Failed to complete operation' };
      }
      showSnackbar('Operation completed', 'success');
      fetchOperations(woId);
    } catch (err: any) {
      showSnackbar(err?.message || 'Failed to complete operation', 'error');
    }
  };

  const getOperationStatusColor = (
    status: WorkOrderOperation['status']
  ): 'default' | 'info' | 'success' | 'error' | 'warning' => {
    switch (status) {
      case 'pending':     return 'default';
      case 'planned':     return 'default';
      case 'in_progress': return 'info';
      case 'completed':   return 'success';
      case 'cancelled':   return 'error';
      default:            return 'default';
    }
  };

  // ---- Task 3: Process routes ----

  const loadProcessRoutes = useCallback(async () => {
    setProcessRoutesLoading(true);
    try {
      const res = await fetch(`${API}/api/v1/work-orders/process-routes`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        // Non-fatal: routes are optional
        setProcessRoutes([]);
        return;
      }
      const json = await res.json().catch(() => ({}));
      const raw: any[] = Array.isArray(json)
        ? json
        : Array.isArray(json?.data)
        ? json.data
        : Array.isArray(json?.processRoutes)
        ? json.processRoutes
        : [];
      setProcessRoutes(
        raw.map((r: any) => ({
          id: String(r.id ?? r._id ?? ''),
          name: r.name ?? r.routeName ?? '',
          routeCode: r.routeCode ?? r.code ?? '',
          operations: r.operations,
        }))
      );
    } catch {
      setProcessRoutes([]);
    } finally {
      setProcessRoutesLoading(false);
    }
  }, []);

  const loadRouteDetail = useCallback(async (routeId: string) => {
    setRouteDetailLoading(true);
    setSelectedRouteDetail(null);
    try {
      const res = await fetch(`${API}/api/v1/work-orders/process-routes/${routeId}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) return;
      const json = await res.json().catch(() => ({}));
      const r = json?.data ?? json;
      setSelectedRouteDetail({
        id: String(r.id ?? r._id ?? ''),
        name: r.name ?? r.routeName ?? '',
        routeCode: r.routeCode ?? r.code ?? '',
        operations: Array.isArray(r.operations) ? r.operations : [],
      });
    } catch {
      // silently skip — preview is best-effort
    } finally {
      setRouteDetailLoading(false);
    }
  }, []);

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    setSelectedRoute(null);
    setSelectedRouteDetail(null);
    loadProcessRoutes();
  };

  // ---- Helpers ----

  const getStatusColor = (status: WorkOrder['status']) => {
    switch (status) {
      case 'draft':       return 'default';
      case 'planned':     return 'default';
      case 'released':    return 'info';
      case 'started':     return 'warning';
      case 'in_progress': return 'warning';
      case 'completed':   return 'success';
      case 'cancelled':   return 'error';
      case 'on_hold':     return 'error';
      default:            return 'default';
    }
  };

  const getPriorityColor = (priority: WorkOrder['priority']) => {
    switch (priority) {
      case 'low':    return 'success';
      case 'medium': return 'warning';
      case 'high':   return 'error';
      case 'urgent': return 'error';
      default:       return 'default';
    }
  };

  const getStatusIcon = (status: WorkOrder['status']) => {
    switch (status) {
      case 'started':     return <PlayArrow />;
      case 'in_progress': return <PlayArrow />;
      case 'completed':   return <CheckCircle />;
      case 'cancelled':   return <Cancel />;
      case 'on_hold':     return <Warning />;
      default:            return <Assignment />;
    }
  };

  // ---- Action handlers ----

  const handleReleaseWorkOrder = (workOrder: WorkOrder) => {
    releaseWorkOrder(workOrder.id);
  };

  const handleStartWorkOrder = (workOrder: WorkOrder) => {
    startWorkOrder(workOrder.id);
  };

  const handleCompleteWorkOrder = (workOrder: WorkOrder) => {
    completeWorkOrder(workOrder.id);
  };

  const handleProductionUpdate = (workOrder: WorkOrder) => {
    setSelected(workOrder);
    setProductionQuantity(workOrder.completedQuantity);
    setDialogOpen(true);
  };

  const handleSaveProduction = () => {
    if (selectedWorkOrder) {
      dispatch(updateProductionQuantity({ id: selectedWorkOrder.id, quantity: productionQuantity }));
    }
    setDialogOpen(false);
    setSelected(null);
  };

  const handleCreateWorkOrder = async () => {
    setCreateLoading(true);
    setCreateError(null);
    try {
      const payload: any = { ...newWorkOrder };
      if (selectedRoute) {
        payload.processRouteId = selectedRoute.id;
      }
      await dispatch(createWorkOrder(payload)).unwrap();
      setCreateDialogOpen(false);
      setNewWorkOrder(EMPTY_FORM);
      setSelectedRoute(null);
      setSelectedRouteDetail(null);
    } catch (err: any) {
      setCreateError(err?.message ?? 'Failed to create work order. Please try again.');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleRefresh = () => {
    dispatch(clearError());
    dispatch(fetchWorkOrders());
  };

  // ---- Derived lists ----

  const activeWorkOrders = workOrders.filter((wo: WorkOrder) => wo.status === 'started' || wo.status === 'in_progress');
  const pendingWorkOrders = workOrders.filter((wo: WorkOrder) => wo.status === 'released' || wo.status === 'planned' || wo.status === 'draft');
  const completedWorkOrders = workOrders.filter((wo: WorkOrder) => wo.status === 'completed' || wo.status === 'cancelled');

  // Summary KPIs
  const totalQty = workOrders.reduce((sum: number, wo: WorkOrder) => sum + (wo.quantity || 0), 0);
  const completedQty = workOrders.reduce((sum: number, wo: WorkOrder) => sum + (wo.completedQuantity || 0), 0);
  const overallEfficiency = totalQty > 0 ? ((completedQty / totalQty) * 100).toFixed(1) : '0.0';

  // ---- Sub-components ----

  const WorkOrderTable: React.FC<{ orders: WorkOrder[]; showActions?: boolean }> = ({
    orders,
    showActions = true,
  }) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Work Order</TableCell>
            <TableCell>Product</TableCell>
            <TableCell>Progress</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Priority</TableCell>
            <TableCell>Due Date</TableCell>
            <TableCell>Work Center</TableCell>
            {showActions && <TableCell>Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <TableRow
              key={order.id}
              hover
              sx={{ cursor: 'pointer' }}
              onClick={() => handleOpenDetail(order)}
            >
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {order.orderNumber}
                </Typography>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">{order.productName}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {order.productCode}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">
                    {order.completedQuantity} / {order.quantity}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={order.quantity > 0 ? (order.completedQuantity / order.quantity) * 100 : 0}
                    sx={{ mt: 1, height: 6, borderRadius: 3 }}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={order.status.replace('_', ' ').toUpperCase()}
                  color={getStatusColor(order.status)}
                  size="small"
                  icon={getStatusIcon(order.status)}
                />
              </TableCell>
              <TableCell>
                <Chip
                  label={order.priority.toUpperCase()}
                  color={getPriorityColor(order.priority)}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {order.dueDate ? new Date(order.dueDate).toLocaleDateString() : '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{order.workCenter || '—'}</Typography>
              </TableCell>
              {showActions && (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {(order.status === 'draft' || order.status === 'planned') && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        startIcon={<Publish />}
                        onClick={() => handleReleaseWorkOrder(order)}
                      >
                        Release
                      </Button>
                    )}
                    {order.status === 'released' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<PlayArrow />}
                        onClick={() => handleStartWorkOrder(order)}
                      >
                        Start
                      </Button>
                    )}
                    {(order.status === 'started' || order.status === 'in_progress') && (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckCircle />}
                          onClick={() => handleCompleteWorkOrder(order)}
                        >
                          Complete
                        </Button>
                        <IconButton
                          size="small"
                          color="primary"
                          title="Update qty"
                          onClick={() => handleProductionUpdate(order)}
                        >
                          <Edit />
                        </IconButton>
                      </>
                    )}
                    {order.status !== 'completed' && order.status !== 'cancelled' && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<Cancel />}
                        onClick={() => handleOpenCancelDialog(order)}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ---- Route preview helper ----
  const renderRoutePreview = () => {
    if (!selectedRoute) return null;
    if (routeDetailLoading) {
      return (
        <Box display="flex" alignItems="center" gap={1} mt={1}>
          <CircularProgress size={14} />
          <Typography variant="caption">Loading route details…</Typography>
        </Box>
      );
    }
    if (!selectedRouteDetail) return null;
    const ops = selectedRouteDetail.operations;
    if (!ops || ops.length === 0) {
      return (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          No operations defined on this route.
        </Typography>
      );
    }
    const opNames = ops
      .slice()
      .sort((a: any, b: any) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map((o: any) => o.operationName ?? o.name ?? '')
      .filter(Boolean)
      .join(' → ');
    return (
      <Alert severity="info" sx={{ mt: 1 }} icon={<Timeline fontSize="inherit" />}>
        This will create {ops.length} operation{ops.length !== 1 ? 's' : ''}: {opNames}
      </Alert>
    );
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Production Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={isLoading ? <CircularProgress size={16} /> : <Refresh />}
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? 'Loading…' : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreateDialog}
          >
            Create Work Order
          </Button>
        </Box>
      </Box>

      {/* Error banner */}
      {error && (
        <Alert severity="warning" onClose={() => dispatch(clearError())} sx={{ mb: 2 }}>
          {error} — Some data may be sample data.
        </Alert>
      )}

      {/* Loading bar */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Orders
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {isLoading ? <CircularProgress size={28} /> : activeWorkOrders.length}
                  </Typography>
                </Box>
                <PlayArrow sx={{ fontSize: 40, color: 'warning.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Pending Orders
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {isLoading ? <CircularProgress size={28} /> : pendingWorkOrders.length}
                  </Typography>
                </Box>
                <Assignment sx={{ fontSize: 40, color: 'info.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Completed Today
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {isLoading ? <CircularProgress size={28} /> : completedWorkOrders.length}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Completion Rate
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    {isLoading ? <CircularProgress size={28} /> : `${overallEfficiency}%`}
                  </Typography>
                </Box>
                <Speed sx={{ fontSize: 40, color: 'primary.main' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label={`Active Orders (${activeWorkOrders.length})`} />
          <Tab label={`Pending Orders (${pendingWorkOrders.length})`} />
          <Tab label={`Completed Orders (${completedWorkOrders.length})`} />
          <Tab label="All Orders" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {selectedTab === 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>Active Work Orders</Typography>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : activeWorkOrders.length === 0 ? (
            <Alert severity="info">No active work orders at the moment.</Alert>
          ) : (
            <WorkOrderTable orders={activeWorkOrders} />
          )}
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>Pending Work Orders</Typography>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : pendingWorkOrders.length === 0 ? (
            <Alert severity="info">No pending work orders.</Alert>
          ) : (
            <WorkOrderTable orders={pendingWorkOrders} />
          )}
        </Box>
      )}

      {selectedTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>Completed Work Orders</Typography>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : completedWorkOrders.length === 0 ? (
            <Alert severity="info">No completed work orders today.</Alert>
          ) : (
            <WorkOrderTable orders={completedWorkOrders} showActions={false} />
          )}
        </Box>
      )}

      {selectedTab === 3 && (
        <Box>
          <Typography variant="h6" gutterBottom>All Work Orders ({workOrders.length})</Typography>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : workOrders.length === 0 ? (
            <Alert severity="info">No work orders found.</Alert>
          ) : (
            <WorkOrderTable orders={workOrders as WorkOrder[]} />
          )}
        </Box>
      )}

      {/* Production Quantity Update Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Production Quantity</DialogTitle>
        <DialogContent>
          {selectedWorkOrder && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Work Order: {selectedWorkOrder.orderNumber}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Product: {selectedWorkOrder.productName}
              </Typography>
              <TextField
                label="Completed Quantity"
                type="number"
                value={productionQuantity}
                onChange={(e) => setProductionQuantity(Number(e.target.value))}
                fullWidth
                margin="normal"
                inputProps={{ min: 0, max: selectedWorkOrder.quantity }}
              />
              <Typography variant="caption" color="textSecondary">
                Target Quantity: {selectedWorkOrder.quantity}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveProduction} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Task 1: Cancel Work Order Dialog ---- */}
      <Dialog open={cancelDialogOpen} onClose={handleCloseCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Work Order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            This action cannot be undone. Please provide a reason.
          </Typography>
          <TextField
            label="Reason for cancellation"
            multiline
            rows={3}
            fullWidth
            value={cancelReason}
            onChange={(e) => {
              setCancelReason(e.target.value);
              if (cancelReasonError && e.target.value.trim().length >= 10) {
                setCancelReasonError(null);
              }
            }}
            error={!!cancelReasonError}
            helperText={cancelReasonError ?? ''}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={cancelLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmCancel}
            color="error"
            variant="contained"
            disabled={cancelLoading}
            startIcon={cancelLoading ? <CircularProgress size={16} /> : <Cancel />}
          >
            {cancelLoading ? 'Cancelling…' : 'Confirm Cancel'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Task 2: Work Order Detail + Operations Dialog ---- */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Work Order Detail — {detailWorkOrder?.orderNumber}
        </DialogTitle>
        <DialogContent>
          {detailWorkOrder && (
            <Box>
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Product</Typography>
                  <Typography variant="body1">{detailWorkOrder.productName}</Typography>
                  <Typography variant="caption" color="textSecondary">{detailWorkOrder.productCode}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Status</Typography>
                  <Box mt={0.5}>
                    <Chip
                      label={detailWorkOrder.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(detailWorkOrder.status)}
                      size="small"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Quantity</Typography>
                  <Typography variant="body1">{detailWorkOrder.completedQuantity} / {detailWorkOrder.quantity}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="textSecondary">Due Date</Typography>
                  <Typography variant="body1">
                    {detailWorkOrder.dueDate ? new Date(detailWorkOrder.dueDate).toLocaleDateString() : '—'}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>Operations</Typography>

              {operationsLoading && (
                <Box display="flex" alignItems="center" gap={2} p={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Loading operations…</Typography>
                </Box>
              )}

              {operationsError && (
                <Alert severity="error" sx={{ mb: 2 }}>{operationsError}</Alert>
              )}

              {!operationsLoading && !operationsError && operations.length === 0 && (
                <Alert severity="info">No operations defined for this work order.</Alert>
              )}

              {!operationsLoading && operations.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Seq</TableCell>
                        <TableCell>Operation Name</TableCell>
                        <TableCell>Work Center</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Planned Qty</TableCell>
                        <TableCell>Actual Qty</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {operations
                        .slice()
                        .sort((a, b) => a.sequence - b.sequence)
                        .map((op) => (
                          <TableRow key={op.id}>
                            <TableCell>{op.sequence}</TableCell>
                            <TableCell>{op.operationName || '—'}</TableCell>
                            <TableCell>{op.workCenter || '—'}</TableCell>
                            <TableCell>
                              <Chip
                                label={op.status.replace('_', ' ').toUpperCase()}
                                color={getOperationStatusColor(op.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{op.plannedQuantity}</TableCell>
                            <TableCell>{op.actualQuantity}</TableCell>
                            <TableCell>
                              <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                {(op.status === 'pending' || op.status === 'planned') && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    startIcon={<PlayArrow />}
                                    onClick={() => handleOperationStart(detailWorkOrder.id, op.id)}
                                  >
                                    Start
                                  </Button>
                                )}
                                {op.status === 'in_progress' && (
                                  <>
                                    <TextField
                                      label="Actual Qty"
                                      type="number"
                                      size="small"
                                      value={opActualQty[op.id] ?? op.actualQuantity}
                                      onChange={(e) =>
                                        setOpActualQty((prev) => ({
                                          ...prev,
                                          [op.id]: Number(e.target.value),
                                        }))
                                      }
                                      inputProps={{ min: 0 }}
                                      sx={{ width: 100 }}
                                    />
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      startIcon={<CheckCircle />}
                                      onClick={() => handleOperationComplete(detailWorkOrder.id, op.id)}
                                    >
                                      Complete
                                    </Button>
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Lifecycle action Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ---- Create Work Order Dialog (Task 3: includes Process Route) ---- */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Work Order</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }}>{createError}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Order Number"
                  value={newWorkOrder.orderNumber}
                  onChange={(e) => setNewWorkOrder(prev => ({ ...prev, orderNumber: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Product Code"
                  value={newWorkOrder.productCode}
                  onChange={(e) => setNewWorkOrder(prev => ({ ...prev, productCode: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Product Name"
                  value={newWorkOrder.productName}
                  onChange={(e) => setNewWorkOrder(prev => ({ ...prev, productName: e.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={newWorkOrder.quantity}
                  onChange={(e) => setNewWorkOrder(prev => ({ ...prev, quantity: Number(e.target.value) }))}
                  fullWidth
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Work Center"
                  value={newWorkOrder.workCenter}
                  onChange={(e) => setNewWorkOrder(prev => ({ ...prev, workCenter: e.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={newWorkOrder.priority}
                    label="Priority"
                    onChange={(e) => setNewWorkOrder(prev => ({ ...prev, priority: e.target.value as WorkOrder['priority'] }))}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newWorkOrder.status}
                    label="Status"
                    onChange={(e) => setNewWorkOrder(prev => ({ ...prev, status: e.target.value as WorkOrder['status'] }))}
                  >
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="planned">Planned</MenuItem>
                    <MenuItem value="released">Released</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={newWorkOrder.startDate?.split('T')[0] ?? ''}
                  onChange={(e) => setNewWorkOrder(prev => ({ ...prev, startDate: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Due Date"
                  type="date"
                  value={newWorkOrder.dueDate?.split('T')[0] ?? ''}
                  onChange={(e) => setNewWorkOrder(prev => ({ ...prev, dueDate: e.target.value }))}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Assigned Operator"
                  value={newWorkOrder.assignedOperator}
                  onChange={(e) => setNewWorkOrder(prev => ({ ...prev, assignedOperator: e.target.value }))}
                  fullWidth
                />
              </Grid>

              {/* Task 3: Process Route field */}
              <Grid item xs={12}>
                <Autocomplete
                  options={processRoutes}
                  loading={processRoutesLoading}
                  getOptionLabel={(option) =>
                    option.name ? `${option.name} (${option.routeCode})` : option.routeCode
                  }
                  value={selectedRoute}
                  onChange={(_e, value) => {
                    setSelectedRoute(value);
                    setSelectedRouteDetail(null);
                    if (value) {
                      loadRouteDetail(value.id);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Process Route (optional)"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {processRoutesLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
                {renderRoutePreview()}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateDialogOpen(false);
              setNewWorkOrder(EMPTY_FORM);
              setCreateError(null);
              setSelectedRoute(null);
              setSelectedRouteDetail(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateWorkOrder}
            variant="contained"
            disabled={createLoading || !newWorkOrder.orderNumber || !newWorkOrder.productName}
            startIcon={createLoading ? <CircularProgress size={16} /> : <Add />}
          >
            {createLoading ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Production;
